import { useEffect } from 'react';
import { create } from 'zustand';
import { getOrgMembers, getCrmCustomers, OrgMember, CrmCustomer } from '../services/api';

/**
 * 選單用的參考資料：組織成員（任務負責人）、CRM 客戶（專案客戶）
 * 每次開啟網頁抓一次，各頁共用；顯示名稱用 memberName／customerName
 */
interface LookupState {
  members: OrgMember[];
  customers: CrmCustomer[];
  membersLoaded: boolean;
  customersLoaded: boolean;
  loadMembers: () => Promise<void>;
  loadCustomers: () => Promise<void>;
}

let membersRequest: Promise<void> | null = null;
let customersRequest: Promise<void> | null = null;

const useLookupStore = create<LookupState>()((set) => ({
  members: [],
  customers: [],
  membersLoaded: false,
  customersLoaded: false,
  loadMembers: () =>
    (membersRequest ??= getOrgMembers()
      .then(res => set({ members: res.data, membersLoaded: true }))
      .catch(error => {
        membersRequest = null; // 失敗時下次再試
        console.error('Failed to load members:', error);
      })),
  loadCustomers: () =>
    (customersRequest ??= getCrmCustomers()
      .then(res => set({ customers: res.data, customersLoaded: true }))
      .catch(error => {
        customersRequest = null;
        console.error('Failed to load CRM customers:', error);
      })),
}));

/** 組織成員（第一次用到時載入） */
export const useMembers = () => {
  const { members, loadMembers } = useLookupStore();
  useEffect(() => { loadMembers(); }, [loadMembers]);
  return members;
};

/** CRM 客戶（第一次用到時載入） */
export const useCrmCustomers = () => {
  const { customers, loadCustomers } = useLookupStore();
  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  return customers;
};

/** 負責人顯示名稱：找得到成員顯示姓名（沒填姓名用 email）；舊資料是手打的文字就照原樣顯示 */
export const memberName = (members: OrgMember[], id?: string | null) => {
  if (!id) return '';
  const member = members.find(m => m.id === id);
  return member ? member.name || member.email : id;
};

export const customerName = (customers: CrmCustomer[], id?: string | null) => {
  if (!id) return '';
  const customer = customers.find(c => c.id === id);
  return customer ? customer.name : id;
};

/** 下拉選項：目前的值不在清單裡（舊資料手打的文字、已離職成員）時也要列出，才不會顯示成空白 */
export const withCurrentValue = (options: { value: string; label: string }[], current?: string | null) =>
  current && !options.some(o => o.value === current) ? [...options, { value: current, label: current }] : options;
